
import {Vue, Component, Prop, Watch} from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import {COL, sqlSum, sqlSumByDocType, getTableName} from '@/worker/db/sqlHelpers';
import FilterWidget from '../shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import {roundToFixed} from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import ShowProfitToggle from '@/components/ShowProfitToggle.vue';
import settingx from '@/store/modules/settingx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';

@Component({
    components: {
        AmountDisplayerFilterable,
        ShowProfitToggle,
        IncludeCreditNoteToggle,
        IncludeDebitNoteToggle,
        SlPhModuleOnlyToggle,
    },
})
export default class SalesProfitNumberDisplayer extends FilterWidget {
    public filterIds: Array<
        | 'date'
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'docproject'
        | 'dtlproject'
    > = [];

    public profitPercentage = 0;
    public profitAmount = '' || 0;

    public get permissionIds(): PermissionsGroup[] {
        return ['customers', 'sales'];
    }

    public get title() {
        return this.showProfit ? 'Sales Profit' : 'Sales Margin';
    }

    public get showProfit() {
        return settingx.now.showProfit;
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public get currentYear() {
        return moment(this.selectedAsOfDate).year();
    }

    public get currentMonthRange(): [number, number] {
        return [
            moment(this.selectedAsOfDate).startOf('month').valueOf(),
            moment(this.selectedAsOfDate).endOf('month').valueOf(),
        ];
    }

    public get currency() {
        return gdbx.currencySymbol;
    }

    public get decimal() {
        return gdbx.numDecimal;
    }

    public get expensiveHook() {
        const {
            selectedDateRange,
            selectedAsOfDate,
            selectedStockItems,
            selectedDocProjects,
            selectedDTLProjects,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        } = this;
        return JSON.stringify([
            selectedDateRange,
            selectedAsOfDate,
            selectedStockItems,
            selectedDocProjects,
            selectedDTLProjects,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        ]);
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.3);

        // Build filter clauses manually for cross-table UNION
        const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
        const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;

        let docProjectFilter = '';
        if (this.selectedDocProjects.length > 0) {
            const list = this.selectedDocProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            docProjectFilter = ` AND "DOCPROJECT" IN (${list})`;
        }

        let dtlProjectFilter = '';
        if (this.selectedDTLProjects.length > 0) {
            const list = this.selectedDTLProjects.map((p) => `'${escapeStr(p)}'`).join(',');
            dtlProjectFilter = ` AND "DTLPROJECT" IN (${list})`;
        }

        const baseFilter = `${dateFilter}${docProjectFilter}${dtlProjectFilter}`;

        // Get table names
        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const arTable = getTableName('customerTrades');

        // Get a head for executing queries (no filters needed)
        // Ensure all tables are loaded before querying
        const queryHead = await this._ensureTablesLoaded([
            'sales', 'creditNotes', 'customerTrades',
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.3, 0.9);

        // Run SL and AR queries in parallel
        const [slResult, arResult] = await Promise.all([
            // Single UNION query for SL data (sales + creditNotes) with amounts and profits
            queryHead.rawQuery(`
                SELECT
                    SUM(CASE WHEN src = 'sales' AND docType IN ('IV', 'CS') THEN amount ELSE 0 END) as iv_cs_amount,
                    SUM(CASE WHEN src = 'sales' AND docType IN ('IV', 'CS') THEN profit ELSE 0 END) as iv_cs_profit,
                    SUM(CASE WHEN src = 'sales' AND docType = 'DN' THEN amount ELSE 0 END) as dn_amount,
                    SUM(CASE WHEN src = 'sales' AND docType = 'DN' THEN profit ELSE 0 END) as dn_profit,
                    SUM(CASE WHEN src = 'cn' THEN amount ELSE 0 END) as cn_amount,
                    SUM(CASE WHEN src = 'cn' THEN profit ELSE 0 END) as cn_profit
                FROM (
                    SELECT 'sales' as src, ${COL.DOCTYPE} as docType,
                        TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
                        TRY_CAST(${COL.PROFIT} AS DOUBLE) as profit
                    FROM ${salesTable}
                    WHERE ${baseFilter}
                    UNION ALL
                    SELECT 'cn' as src, 'CN' as docType,
                        TRY_CAST(${COL.AMOUNT} AS DOUBLE) as amount,
                        TRY_CAST(${COL.PROFIT} AS DOUBLE) as profit
                    FROM ${cnTable}
                    WHERE ${baseFilter}
                ) AS combined
            `),
            // Single query for AR data
            queryHead.rawQuery(`
                SELECT
                    ${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
                    ${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
                    ${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')}
                FROM ${arTable}
                WHERE ${baseFilter}
            `),
        ]);
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.95);

        // Extract SL values
        const slRow = slResult[0] || {};
        const slIvCsAmount = Number(slRow.iv_cs_amount) || 0;
        const slIvCsProfit = Number(slRow.iv_cs_profit) || 0;
        const slDnAmount = Number(slRow.dn_amount) || 0;
        const slDnProfit = Number(slRow.dn_profit) || 0;
        const slCnAmount = Number(slRow.cn_amount) || 0;
        const slCnProfit = Number(slRow.cn_profit) || 0;

        // Extract AR values
        const arRow = arResult[0] || {};
        const arIvAmount = Number(arRow.iv_amount) || 0;
        const arCnAmount = Number(arRow.cn_amount) || 0;
        const arDnAmount = Number(arRow.dn_amount) || 0;

        // Calculate SL totals (with toggles)
        const slAmount = slIvCsAmount
            - (this.includeCreditNote ? slCnAmount : 0)
            + (this.includeDebitNote ? slDnAmount : 0);
        const slProfit = slIvCsProfit
            - (this.includeCreditNote ? slCnProfit : 0)
            + (this.includeDebitNote ? slDnProfit : 0);

        let salesAmount: number;
        let salesProfit: number;

        if (this.slPhModuleOnly) {
            // SL Mode - use SL data only
            salesAmount = slAmount;
            salesProfit = slProfit;
        } else {
            // AR Mode - calculate AR amount and derive profit
            const arAmount = arIvAmount
                - (this.includeCreditNote ? arCnAmount : 0)
                + (this.includeDebitNote ? arDnAmount : 0);

            // AR Profit = SL Profit + Extra Profit (AR Amount - SL Amount)
            const extraProfit = arAmount - slAmount;

            salesAmount = arAmount;
            salesProfit = slProfit + extraProfit;
        }

        this.profitAmount = roundToFixed(salesProfit, this.decimal);

        this.profitPercentage = roundToFixed(
            salesAmount !== 0 ? (salesProfit / salesAmount) * 100 : 0,
            this.decimal,
        );

        this.generateLoadingText(1);
        this.saveHistory('profitAmount', 'profitPercentage');
    }
}
